import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface User {
    id: string
    name: string
    email: string
    role: 'student' | 'mentor' | 'admin' | 'sub-admin' | 'counsellor'
    avatar?: string
}

interface AuthContextType {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    login: (role: 'student' | 'mentor' | 'admin' | 'sub-admin' | 'counsellor', email: string) => Promise<void>
    logout: () => void
    signup: (userData: Partial<User>) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        // Check for persisted user session
        const storedUser = localStorage.getItem('neet_user')
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser))
            } catch (error) {
                console.error('Failed to parse user session', error)
                localStorage.removeItem('neet_user')
            }
        }
        setIsLoading(false)
    }, [])

    const login = async (role: 'student' | 'mentor' | 'admin' | 'sub-admin' | 'counsellor', email: string) => {
        setIsLoading(true)
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))

        const mockUser: User = {
            id: role === 'student' ? 'student-123' : role === 'mentor' ? 'mentor-456' : role === 'admin' ? 'admin-001' : role === 'sub-admin' ? 'sub-admin-001' : 'counsellor-001',
            name: role === 'student' ? 'Arun Student' : role === 'mentor' ? 'Dr. Sarah Patel' : role === 'admin' ? 'Admin' : role === 'sub-admin' ? 'Sub Admin' : 'Counsellor',
            email: email,
            role: role,
            avatar: role === 'student' ? 'AS' : role === 'mentor' ? 'SP' : role === 'admin' ? 'AD' : role === 'sub-admin' ? 'SA' : 'CO'
        }

        setUser(mockUser)
        localStorage.setItem('neet_user', JSON.stringify(mockUser))
        setIsLoading(false)
    }

    const signup = async (userData: Partial<User>) => {
        setIsLoading(true)
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500))

        const newUser: User = {
            id: `user-${Date.now()}`,
            name: userData.name || 'New User',
            email: userData.email || '',
            role: 'student', // Default to student for signup
            avatar: userData.name ? userData.name.substring(0, 2).toUpperCase() : 'NU'
        }

        setUser(newUser)
        localStorage.setItem('neet_user', JSON.stringify(newUser))
        setIsLoading(false)
    }

    const logout = () => {
        setUser(null)
        localStorage.removeItem('neet_user')
    }

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout, signup }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
